import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import UilApps from '@iconscout/react-unicons/icons/uil-apps';
import UilBag from '@iconscout/react-unicons/icons/uil-bag';
import UilUserArrows from '@iconscout/react-unicons/icons/uil-user-arrows';
import UilFolderOpen from '@iconscout/react-unicons/icons/uil-folder-open';
import UilFileCopyAlt from '@iconscout/react-unicons/icons/uil-file-copy-alt';
import UilFileRedoAlt from '@iconscout/react-unicons/icons/uil-file-redo-alt';
import UilAngleDown from '@iconscout/react-unicons/icons/uil-angle-down';
import propTypes from 'prop-types';

// ── Design tokens ──────────────────────────────────────────────────────────────
const TEXT_MUTED   = 'rgba(255,255,255,0.4)';   // section labels
const TEXT_DEFAULT = 'rgba(255,255,255,0.72)';  // idle items
const TEXT_ACTIVE  = '#ffffff';                 // active item text
const ACTIVE_BG    = 'rgba(255,255,255,0.12)';  // active item fill
const ACTIVE_DOT   = '#6ee7b7';                 // left accent dot / border (emerald-300)

// ── NavItem ────────────────────────────────────────────────────────────────────
function NavItem({ to, icon: Icon, label, onClick, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 14px',
        borderRadius: 8,
        margin: '1px 8px',
        textDecoration: 'none',
        fontSize: 13.5,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? TEXT_ACTIVE : TEXT_DEFAULT,
        background: isActive ? ACTIVE_BG : 'transparent',
        position: 'relative',
        transition: 'background 0.12s, color 0.12s',
      })}
    >
      {({ isActive }) => (
        <>
          {/* Active accent dot */}
          {isActive && (
            <span style={{
              position: 'absolute',
              left: 0, top: '50%',
              transform: 'translateY(-50%)',
              width: 3, height: 18,
              borderRadius: '0 3px 3px 0',
              background: ACTIVE_DOT,
            }} />
          )}
          {Icon && (
            <Icon style={{
              width: 17, height: 17, flexShrink: 0,
              color: isActive ? ACTIVE_DOT : 'rgba(255,255,255,0.45)',
            }} />
          )}
          <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {label}
          </span>
        </>
      )}
    </NavLink>
  );
}

NavItem.propTypes = {
  to: propTypes.string.isRequired,
  icon: propTypes.elementType,
  label: propTypes.string.isRequired,
  onClick: propTypes.func,
  end: propTypes.bool,
};

// ── NavGroup (collapsible) ─────────────────────────────────────────────────────
function NavGroup({ icon: Icon, label, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 14px', margin: '1px 8px',
          width: 'calc(100% - 16px)',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13.5, fontWeight: 400, color: TEXT_DEFAULT,
          borderRadius: 8,
          transition: 'background 0.12s',
        }}
      >
        {Icon && <Icon style={{ width: 17, height: 17, flexShrink: 0, color: 'rgba(255,255,255,0.45)' }} />}
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
          {label}
        </span>
        <UilAngleDown style={{
          width: 15, height: 15, flexShrink: 0, color: 'rgba(255,255,255,0.35)',
          transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 0.18s',
        }} />
      </button>

      {open && (
        <div style={{ paddingLeft: 16, marginTop: 2 }}>
          {children}
        </div>
      )}
    </div>
  );
}

NavGroup.propTypes = {
  icon: propTypes.elementType,
  label: propTypes.string.isRequired,
  children: propTypes.node.isRequired,
};

// ── SectionLabel ───────────────────────────────────────────────────────────────
function SectionLabel({ label }) {
  return (
    <p style={{
      fontSize: 10.5, fontWeight: 600, letterSpacing: '0.09em',
      textTransform: 'uppercase', color: TEXT_MUTED,
      padding: '18px 20px 5px',
      margin: 0,
    }}>
      {label}
    </p>
  );
}

SectionLabel.propTypes = { label: propTypes.string.isRequired };

// ── MenuItems ──────────────────────────────────────────────────────────────────
function MenuItems({ toggleCollapsed }) {
  const { t } = useTranslation();

  const role     = localStorage.getItem('user_role') || '';
  const userRole = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  const path     = `/${userRole}`.toLowerCase();

  const close = () => { if (toggleCollapsed) toggleCollapsed(); };

  return (
    <nav style={{ padding: '8px 0 32px' }}>

      {/* ── Company & Filer ── */}
      {(userRole === 'Company' || userRole === 'Filer') && (
        <>
          <SectionLabel label={t('Overview')} />
          <NavItem to={path} icon={UilApps} label={t('Dashboard')} onClick={close} end />

          <SectionLabel label={t('Filings')} />
          <NavItem to={`${path}/forms`} icon={UilFileCopyAlt} label={t('Forms')} onClick={close} />

          {userRole === 'Company' && (
            <>
              <SectionLabel label={t('Account')} />
              <NavItem to={`${path}/authorized-filers`} icon={UilUserArrows} label={t('Authorized Filers')} onClick={close} />
            </>
          )}
        </>
      )}

      {/* ── ERMD ── */}
      {userRole === 'Ermd' && (
        <>
          <SectionLabel label={t('Overview')} />
          <NavItem to={path} icon={UilApps} label={t('Dashboard')} onClick={close} end />

          <SectionLabel label={t('Management')} />
          <NavItem to={`${path}/companies-list`}     icon={UilBag}         label={t('Company Management')} onClick={close} />
          <NavItem to={`${path}/authorized-filers`}  icon={UilUserArrows}  label="Authorized Filers"       onClick={close} />
          <NavGroup icon={UilFolderOpen} label={t('Forms Management')}>
            <NavItem to={`${path}/submissions-list`}      icon={UilFileCopyAlt}  label={t('Forms')}           onClick={close} />
            <NavItem to={`${path}/submissions-reclassify`} icon={UilFileRedoAlt} label={t('Reclassify Form')} onClick={close} />
          </NavGroup>
        </>
      )}

      {/* ── SOD ── */}
      {userRole === 'Sod' && (
        <>
          <SectionLabel label={t('Overview')} />
          <NavItem to={path} icon={UilApps} label={t('Dashboard')} onClick={close} end />

          <SectionLabel label={t('Management')} />
          <NavItem to={`${path}/companies-list`}    icon={UilBag}        label={t('Company Management')} onClick={close} />
          <NavItem to={`${path}/authorized-filers`} icon={UilUserArrows} label="Authorized Filers"       onClick={close} />
          <NavGroup icon={UilFolderOpen} label={t('Forms Management')}>
            <NavItem to={`${path}/submissions-list`} icon={UilFileCopyAlt} label={t('Forms')} onClick={close} />
          </NavGroup>
        </>
      )}
    </nav>
  );
}

MenuItems.propTypes = {
  toggleCollapsed: propTypes.func,
};

export default MenuItems;
